import { Link } from 'react-router-dom';
import { Users, ArrowRight } from 'lucide-react';
import { AppLayout, LoadingSpinner } from './Layout';
import { useAuth } from '../context/AuthContext';
import { useFPContext } from '../context/FuturePathContext';

/**
 * The parent and guardian tools, only for parents and guardians.
 *
 * These pages were reachable by anyone, so a student who wandered in got a wall of failed
 * requests, "Only parents can view children" as a red error, instead of an explanation. The
 * context engine already knows whether someone is here to support another person.
 */
export const ParentOnly = ({ children }) => {
  const { user } = useAuth();
  const { ready, traits, loading } = useFPContext();
  const isParent = user?.role === 'parent' || user?.role === 'guardian'
    || user?.role === 'admin' || user?.role === 'super_admin'
    || traits?.supporter === true;

  if (loading && !ready) {
    return <AppLayout><div className="flex justify-center py-24"><LoadingSpinner size="lg" /></div></AppLayout>;
  }
  if (isParent) return children;

  return (
    <AppLayout>
      <div className="max-w-xl" data-testid="parent-only-notice">
        <Users className="w-8 h-8 text-cyan-300" />
        <h1 className="font-outfit text-3xl font-semibold text-white mt-4">
          This part is for parents and guardians
        </h1>
        <p className="text-base text-slate-300/90 mt-3 leading-relaxed">
          It holds the tools an adult uses to support someone else's decision, what the family can
          contribute, the conversations worth having, and the paperwork that comes with them. Your
          own FuturePath is where the work you're doing lives.
        </p>
        <div className="flex flex-wrap gap-3 mt-7">
          <Link to="/app/dashboard" data-testid="parent-only-dashboard"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-300 text-slate-950 text-sm font-semibold hover:bg-cyan-200 transition-colors">
            Back to my FuturePath <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/app/financial" data-testid="parent-only-money"
            className="inline-flex items-center px-5 py-2.5 rounded-full border border-slate-700 text-sm text-slate-200 hover:border-slate-500 transition-colors">
            See what my path costs
          </Link>
        </div>
        <p className="text-xs text-slate-300 mt-6">
          If you are a parent and you're seeing this, your account is set up as a student, an admin
          can change that in a moment.
        </p>
      </div>
    </AppLayout>
  );
};

export default ParentOnly;
